"use client";

// `SITE-SPD02`: the styles this text needs, carried by the component (`components/rich-content.tsx`).
import "@/styles/progsity-content.css";
import "katex/dist/katex.min.css";
import { useEffect, useRef } from "react";
import { renderMathNodes } from "@/lib/render-math";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { cn } from "@/lib/utils";
import { enhanceCodeBlocks } from "../lib/enhance-code-blocks";

/** Cheap "does this look like markup" test — most quiz text is plain. */
const HAS_TAGS = /<\/?[a-z][^>]*>/i;

interface QuizRichTextProps {
  html?: string;
  /** span for inline spots (option labels inside a <button>), div otherwise. */
  as?: "div" | "span";
  className?: string;
}

/**
 * Quiz question/option/explanation text is stored as sanitized rich HTML, so a
 * "predict the output" question can carry a real <pre><code> block. Plain-text
 * questions (the common case) render without any prose wrapper. Code blocks get
 * hljs highlighting but no copy chrome — a quiz snippet is meant to be read.
 */
export function QuizRichText({ html, as = "div", className }: QuizRichTextProps) {
  const ref = useRef<HTMLElement>(null);
  const rich = Boolean(html && HAS_TAGS.test(html));

  useEffect(() => {
    if (rich && ref.current) enhanceCodeBlocks(ref.current, { chrome: false });
  }, [rich, html]);

  if (!html) return null;

  if (!rich) {
    return as === "span" ? (
      <span className={className}>{html}</span>
    ) : (
      <div className={className}>{html}</div>
    );
  }

  const Tag = as;
  return (
    <Tag
      ref={ref as React.Ref<HTMLDivElement & HTMLSpanElement>}
      className={cn("progsity-content pc-quiz-rich", className)}
      dangerouslySetInnerHTML={{ __html: renderMathNodes(sanitizeHtml(html)) }}
    />
  );
}
